import React, { useEffect, useState } from 'react';
import { FiPlus, FiEdit, FiTrash2, FiUsers } from 'react-icons/fi';
import { adminAPI, studentGroupsAPI } from '../../../services/api';
import './StudentGroupsPage.css';

const emptyForm = {
    name: '',
    description: '',
    studentIds: []
};

const StudentGroupsPage = () => {
    const [groups, setGroups] = useState([]);
    const [students, setStudents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [editingGroup, setEditingGroup] = useState(null);
    const [formData, setFormData] = useState(emptyForm);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            setLoading(true);
            const [groupsRes, studentsRes] = await Promise.all([
                studentGroupsAPI.getGroups(),
                adminAPI.getStudents()
            ]);
            setGroups(groupsRes.data.groups || []);
            setStudents(studentsRes.data.students || []);
            setError('');
        } catch (err) {
            console.error(err);
            setError('Ошибка при загрузке данных');
        } finally {
            setLoading(false);
        }
    };

    const openCreate = () => {
        setEditingGroup(null);
        setFormData(emptyForm);
        setShowModal(true);
    };

    const openEdit = async (group) => {
        try {
            const res = await studentGroupsAPI.getGroup(group.id);
            setEditingGroup(group);
            setFormData({
                name: res.data.group.name,
                description: res.data.group.description || '',
                studentIds: (res.data.members || []).map(m => m.id)
            });
            setShowModal(true);
        } catch (err) {
            console.error(err);
            alert('Не удалось загрузить группу');
        }
    };

    const closeModal = () => {
        setShowModal(false);
        setEditingGroup(null);
        setFormData(emptyForm);
    };

    const toggleStudent = (studentId) => {
        setFormData(prev => {
            const id = Number(studentId);
            const exists = prev.studentIds.includes(id);
            return {
                ...prev,
                studentIds: exists
                    ? prev.studentIds.filter(sid => sid !== id)
                    : [...prev.studentIds, id]
            };
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.name.trim()) {
            alert('Укажите название группы');
            return;
        }

        try {
            setSaving(true);
            const payload = {
                name: formData.name.trim(),
                description: formData.description.trim() || null,
                studentIds: formData.studentIds
            };

            if (editingGroup) {
                await studentGroupsAPI.updateGroup(editingGroup.id, payload);
            } else {
                await studentGroupsAPI.createGroup(payload);
            }

            closeModal();
            loadData();
        } catch (err) {
            console.error(err);
            alert(err.response?.data?.error || 'Ошибка при сохранении группы');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (group) => {
        if (!window.confirm(`Удалить группу «${group.name}»?`)) return;

        try {
            await studentGroupsAPI.deleteGroup(group.id);
            loadData();
        } catch (err) {
            console.error(err);
            alert(err.response?.data?.error || 'Ошибка при удалении группы');
        }
    };

    const studentLabel = (student) => {
        const fullName = [student.first_name, student.last_name].filter(Boolean).join(' ');
        return fullName || student.username;
    };

    if (loading) {
        return <div className="student-groups-loading">Загрузка...</div>;
    }

    return (
        <div className="student-groups-page">
            <div className="student-groups-header">
                <div>
                    <h1>Группы учеников</h1>
                    <p className="student-groups-subtitle">
                        Создайте группы, чтобы назначать ДЗ и материалы сразу всем участникам
                    </p>
                </div>
                <button type="button" className="btn-create-group" onClick={openCreate}>
                    <FiPlus />
                    Создать группу
                </button>
            </div>

            {error && <div className="student-groups-error">{error}</div>}

            {groups.length === 0 ? (
                <div className="student-groups-empty">
                    <FiUsers size={48} color="#ccc" />
                    <p>Групп пока нет</p>
                    <button type="button" className="btn-create-group" onClick={openCreate}>
                        Создать первую группу
                    </button>
                </div>
            ) : (
                <div className="student-groups-table">
                    <table>
                        <thead>
                            <tr>
                                <th>Название</th>
                                <th>Учеников</th>
                                <th>Описание</th>
                                <th>Действия</th>
                            </tr>
                        </thead>
                        <tbody>
                            {groups.map(group => (
                                <tr key={group.id}>
                                    <td className="group-name-cell">{group.name}</td>
                                    <td>{group.members_count || 0}</td>
                                    <td className="group-desc-cell">{group.description || '—'}</td>
                                    <td>
                                        <div className="table-actions">
                                            <button
                                                type="button"
                                                className="btn-icon"
                                                onClick={() => openEdit(group)}
                                                title="Редактировать"
                                            >
                                                <FiEdit />
                                            </button>
                                            <button
                                                type="button"
                                                className="btn-icon btn-danger"
                                                onClick={() => handleDelete(group)}
                                                title="Удалить"
                                            >
                                                <FiTrash2 />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {showModal && (
                <div className="modal-overlay" onClick={closeModal}>
                    <div className="modal-content group-modal" onClick={(e) => e.stopPropagation()}>
                        <h2>{editingGroup ? 'Редактировать группу' : 'Новая группа'}</h2>
                        <form onSubmit={handleSubmit}>
                            <div className="form-group">
                                <label>Название *</label>
                                <input
                                    type="text"
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    placeholder="Например: Группа A1"
                                    required
                                />
                            </div>
                            <div className="form-group">
                                <label>Описание</label>
                                <textarea
                                    value={formData.description}
                                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                    placeholder="Необязательно"
                                    rows={2}
                                />
                            </div>
                            <div className="form-group">
                                <label>Ученики ({formData.studentIds.length})</label>
                                <div className="group-students-list">
                                    {students.map(student => (
                                        <label
                                            key={student.id}
                                            className={`group-student-item ${formData.studentIds.includes(student.id) ? 'selected' : ''}`}
                                        >
                                            <span className="group-student-check">
                                                <input
                                                    type="checkbox"
                                                    checked={formData.studentIds.includes(student.id)}
                                                    onChange={() => toggleStudent(student.id)}
                                                />
                                            </span>
                                            <span className="group-student-name">{studentLabel(student)}</span>
                                        </label>
                                    ))}
                                </div>
                            </div>
                            <div className="modal-actions">
                                <button type="button" className="btn-cancel" onClick={closeModal} disabled={saving}>
                                    Отмена
                                </button>
                                <button type="submit" className="btn-save" disabled={saving}>
                                    {saving ? 'Сохранение...' : editingGroup ? 'Сохранить' : 'Создать'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default StudentGroupsPage;
